import useIngestMultipleFiles, {
  SUPPORTED_EXTENSIONS,
} from '@/components/Ingest/useIngestMultipleFiles';
import { zoteroModalAtom } from '@/components/Layouts/site';
import { LoadingSpinner } from '@/components/Loading';
import { useTourTarget } from '@/components/tour/useTourTarget';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { DropdownMenuContent } from '@/components/ui/DropdownMenu';
import { Icon } from '@/components/ui/Icon';
import { ReusableTooltip } from '@/components/ui/ReusableTooltip';
import { useAuth } from '@/context/AuthContext';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { useAtom } from 'jotai';
import {
  CircleHelp,
  FileText,
  FolderOpen,
  Shapes,
  UploadIcon,
} from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useRef, useState } from 'react';
import ButtonCard from '../ButtonCard';

export const UploadFileButton = () => {
  const [openMenu, setOpenMenu] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const { processFiles } = useIngestMultipleFiles();
  const { track } = useAnalytics();
  const { user } = useAuth();
  const router = useRouter();
  const [_, setShowZoteroModal] = useAtom(zoteroModalAtom);
  const [routingToZotero, setRoutingToZotero] = useState(false);
  const { onClick } = useTourTarget('upload-button');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      processFiles(Array.from(files));
      e.target.value = '';
    }
  };

  const handleFileOptionClick = () => {
    if (fileInputRef.current) {
      setTimeout(() => {
        fileInputRef.current?.click();
      }, 50);
      track('landing_button_clicked', {
        button: 'upload_file',
      });
    }
  };

  const handleFolderOptionClick = () => {
    if (folderInputRef.current) {
      setTimeout(() => {
        folderInputRef.current?.click();
      }, 50);
      track('landing_button_clicked', {
        button: 'upload_folder',
      });
    }
  };

  const handleZoteroOptionClick = () => {
    if (user?.zoteroKey && user?.zoteroId) {
      setShowZoteroModal(true);
    } else {
      track('zotero_connect', { from: 'app_landing' });
      setRoutingToZotero(true);
      router.push(`/api/zotero/connect?userId=${user?.id}&step=init`);
    }
  };

  return (
    <>
      <input
        id="file-input"
        type="file"
        ref={fileInputRef}
        className="hidden"
        multiple
        accept={SUPPORTED_EXTENSIONS.join(',')}
        onChange={handleFileChange}
      />
      <input
        id="folder-input"
        type="file"
        ref={folderInputRef}
        className="hidden"
        multiple
        onChange={handleFileChange}
        // @ts-ignore - TypeScript doesn't know about these attributes
        webkitdirectory=""
        directory=""
      />

      <DropdownMenu
        open={openMenu}
        onOpenChange={(open) => {
          setOpenMenu(open);
          if (open) {
            onClick();
          }
        }}
      >
        <DropdownMenuTrigger
          asChild
          className="data-[state=open]:bg-background"
        >
          <div id="upload-button">
            <ButtonCard
              className={
                openMenu ? 'bg-popover-hover/40 dark:bg-popover-hover' : ''
              }
              label="Upload"
              icon={UploadIcon}
            />
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={8}
          className="w-[224px] p-0 text-sm leading-[100%] tracking-[-0.32px]"
        >
          <div className="p-0.5">
            <DropdownMenuItem onClick={handleFileOptionClick}>
              <div className="w-full">
                <ReusableTooltip
                  content="Documents, images, audio and video"
                  align="start"
                >
                  <div className="flex w-full flex-row justify-start items-center gap-2">
                    <Icon as={FileText} />
                    File
                  </div>
                </ReusableTooltip>
              </div>
            </DropdownMenuItem>

            {/* Folder option - just visual, actual input is outside dropdown */}
            <DropdownMenuItem onClick={handleFolderOptionClick}>
              <Icon as={FolderOpen} />
              Folder
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleZoteroOptionClick}>
              {routingToZotero ? (
                <LoadingSpinner variant="small" />
              ) : (
                <Icon as={Shapes} />
              )}
              Zotero
            </DropdownMenuItem>
          </div>
          <div className="h-[1px] bg-border" />
          <div className="p-0.5">
            <DropdownMenuItem className="text-textGray4">
              <Link
                href="/docs/import/file-types"
                target="_blank"
                className="flex flex-row gap-2 items-center"
                onClick={() => {
                  track('landing_button_clicked', {
                    button: 'upload_file_learn_file_types',
                  });
                }}
              >
                <Icon as={CircleHelp} />
                Learn about file types
              </Link>
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
